import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { PartnerModel } from '@core/models';
import { CompanyStore } from '@root/app/store/company.store';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs';

type NullOrStringOrStringArray = string | string[] | null;

/**
 * Partner picker with name-search backed by {@link CompanyStore}. Works as a form control
 * (ControlValueAccessor): in `multiple` mode the value is an array of partner OIDs (chips);
 * in single mode it's one OID or `null`.
 */
@Component({
  selector: 'xemex-partner-select',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatButtonModule,
    MatIconModule,
    TranslateModule,
  ],
  templateUrl: './partner-select.component.html',
  styleUrl: './partner-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PartnerSelectComponent),
      multi: true,
    },
  ],
})
export class PartnerSelectComponent implements ControlValueAccessor {
  readonly companyStore = inject(CompanyStore);

  /** Allow picking more than one partner (rendered as chips). */
  readonly multiple = input(false);
  /** Translated field label; omitted when empty. */
  readonly label = input('');
  /** Translated placeholder for the search input. */
  readonly placeholder = input('');

  readonly disabled = signal(false);
  readonly partnerSearchControl = new FormControl('');
  readonly selectedPartners = signal<PartnerModel[]>([]);

  /** Name-search results from the store, minus partners already picked. */
  readonly partnerOptions = computed(() => {
    const chosen = new Set(this.selectedPartners().map(p => p.partnerOid));
    return this.companyStore.partnerSearchResults().filter(p => !chosen.has(p.partnerOid));
  });

  /** In single mode, the one selected partner (if any). */
  readonly singleSelected = computed(() => this.selectedPartners()[0] ?? null);

  /** True once the user has typed a (non-blank) partner query. */
  get hasPartnerQuery(): boolean {
    return !!this.partnerSearchControl.value?.trim();
  }

  private onChange: (value: NullOrStringOrStringArray) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    this.partnerSearchControl.valueChanges
      .pipe(
        // Ignore the object emitted when an option is selected; only react to typed text.
        filter((value): value is string => typeof value === 'string'),
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(),
      )
      .subscribe(query => {
        // Editing the query in single mode invalidates the current pick until a new one is made.
        if (!this.multiple() && this.selectedPartners().length) {
          this.selectedPartners.set([]);
          this.emit();
        }
        this.companyStore.searchPartnersByNameMethod(query.trim());
      });
  }

  /** Loads a default partner list on focus so the dropdown appears without typing. */
  onFocus(): void {
    if (!this.hasPartnerQuery) {
      this.companyStore.searchPartnersByNameMethod('');
    }
  }

  onPartnerSelected(event: MatAutocompleteSelectedEvent): void {
    const partner = event.option.value as PartnerModel;
    if (this.multiple()) {
      this.selectedPartners.update(list =>
        list.some(p => p.partnerOid === partner.partnerOid) ? list : [...list, partner],
      );
      // Reset the input so the user can search for the next partner.
      this.partnerSearchControl.setValue('');
    } else {
      this.selectedPartners.set([partner]);
      // Display the chosen partner's name without triggering another search.
      this.partnerSearchControl.setValue(partner.partnerName, { emitEvent: false });
    }
    this.companyStore.clearPartnerSearch();
    this.emit();
    this.onTouched();
  }

  removePartner(partner: PartnerModel): void {
    this.selectedPartners.update(list => list.filter(p => p.partnerOid !== partner.partnerOid));
    this.emit();
    this.onTouched();
  }

  clearSingle(): void {
    this.selectedPartners.set([]);
    this.partnerSearchControl.setValue('', { emitEvent: false });
    this.companyStore.clearPartnerSearch();
    this.emit();
    this.onTouched();
  }

  private emit(): void {
    const oids = this.selectedPartners().map(p => p.partnerOid);
    this.onChange(this.multiple() ? oids : (oids[0] ?? null));
  }

  // --- ControlValueAccessor ---

  writeValue(value: NullOrStringOrStringArray): void {
    // Only a cleared value is meaningfully writable without partner models to display by name;
    // pre-selecting OIDs would require fetching their names, which no current caller needs.
    if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) {
      this.selectedPartners.set([]);
      this.partnerSearchControl.setValue('', { emitEvent: false });
    }
  }

  registerOnChange(fn: (value: NullOrStringOrStringArray) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
    if (isDisabled) {
      this.partnerSearchControl.disable({ emitEvent: false });
    } else {
      this.partnerSearchControl.enable({ emitEvent: false });
    }
  }
}
